import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { useMediaQuery, useTheme } from '@mui/material'
import * as React from 'react'

/**
 * A reusable confirmation dialog component
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Handler for closing the dialog
 * @param {function} props.onConfirm - Handler for confirming the deletion
 * @param {string} props.title - Dialog title (optional)
 * @param {string} props.content - Dialog content text (optional)
 * @param {React.ReactNode} props.children - Optional children to render inside the dialog content
 * @param {Object} props.actions - Actions to render in the dialog actions
 * @param {Object} props.actions.cancel - Cancel action
 * @param {string} props.actions.cancel.label - Cancel button label
 * @param {function} props.actions.cancel.onClick - Cancel button click handler
 * @param {string} props.actions.confirm.label - Confirm button label
 * @param {function} props.actions.confirm.onClick - Confirm button click handler
 * @param {boolean} props.fullScreenOnSmall - Whether to show the dialog in full screen on small screens
 */
export default function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  content = 'Are you sure you want to perform this action? ',
  children,
  maxWidth = 'sm',
  fullScreenOnSmall = false,
  actions = {
    cancel: {
      label: 'Cancel',
      onClick: onClose,
      color: 'primary',
    },
    confirm: {
      label: 'Confirm',
      onClick: onConfirm,
      color: 'primary',
      variant: '',
      submitting: false,
      disabled: false,
    },
  },
}) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Dialog
      open={open}
      onClose={onClose || actions?.cancel?.onClick}
      onClick={(e) => e.stopPropagation()}
      maxWidth={maxWidth}
      fullScreen={fullScreen && fullScreenOnSmall}
      aria-labelledby="confirmation-dialog-title"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {content && <DialogContentText>{content}</DialogContentText>}
        {children}
      </DialogContent>
      <DialogActions>
        {actions.cancel && (
          <Button onClick={actions.cancel.onClick} variant={actions.cancel.variant} color={actions.cancel.color}>
            {actions.cancel.label}
          </Button>
        )}
        {actions.confirm && !actions.confirm.disabled && (
          <Button
            onClick={actions.confirm.onClick}
            variant={actions.confirm.variant}
            color={actions.confirm.color}
            disabled={actions.confirm.disabled || actions.confirm.submitting}
            startIcon={actions.confirm.submitting ? <CircularProgress size={16} color="inherit" /> : null}
            autoFocus
          >
            {actions.confirm.submitting ? 'Processing...' : actions.confirm.label}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
